import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import confetti from 'canvas-confetti';
import {
  Plus,
  Search,
  Zap,
  Settings2,
  ChevronRight,
  CheckCircle2,
  Circle,
  Loader2,
  ArrowLeft,
  BookOpen,
  Copy,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  AlertTriangle,
  FileText,
  Send,
  ExternalLink,
  Trash2,
  Sparkles,
  Wrench,
  TrendingUp,
  Phone,
  ClipboardList,
  ChevronDown,
  MessageCircle,
  Target,
  BarChart3,
  Users,
  Shield,
  FolderOpen,
  Bell,
  Megaphone,
} from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { avatarSrc } from '../avatarSrc';
import { useNotifications } from '../hooks/useNotifications';
import type { Notification } from '../hooks/useNotifications';
import CoworkHomePage from './CoworkHomePage';
import { AgentCard } from './AgentCard';
import { SkillCard } from './SkillCard';
import { SkillBriefDrawer } from './SkillBriefDrawer';
import { BrandOSPage } from './BrandOSPage';
import { BrandWikiPage } from './BrandWikiPage';
import { ToolsPage } from './ToolsPage';
// SeoAgentPage removed — SEO tools now live in Joanya's chat
import { GoogleAdsAgentPage } from './GoogleAdsAgentPage';
import { AgentChatPage } from './AgentChatPage';
import { ChatPage } from './ChatPage';
import { GrowthDiaryPage } from './GrowthDiaryPage';
import { ContentDetailModal } from './ContentDetailModal';
import { AgentEditModal } from './AgentEditModal';
import { TaskBoardPage } from './TaskBoardPage';
import { OKRPage } from './OKRPage';
import { DashboardsPage } from './DashboardsPage';
import { FilesPage } from './FilesPage';
import { ApprovalsPage } from './ApprovalsPage';
import { CampaignRoomPage } from './CampaignRoomPage';
import { UsagePage } from './UsagePage';
import { OmniSidebar, type PinnedProject, type SidebarView } from './OmniSidebar';
import { ProjectsPage } from './ProjectsPage';
import { ProjectDetailPage } from './ProjectDetailPage';
import { LibraryPage } from './LibraryPage';
import { IntegrationsPage } from './IntegrationsPage';
import { SettingsPage as AccountSettingsPage } from '../shims/SettingsPage';
import { CallInvitePanel } from './CallInvitePanel';
import { CoworkOnboarding } from './CoworkOnboarding';
import { coworkApi } from '../api/coworkApi';
import type { PlanInfo } from '../api/coworkApi';
import { UpgradeModal } from './UpgradeModal';
import { getTeamBrands } from '../shims/beatlyApi';
import { getUserFromStorage } from '@/utils/auth';
import { getTokenFromStorage } from '@/utils/auth';
import { isEmbedMode } from '@/utils/urlParams';
import { useActiveBrand } from '@/cowork/useActiveBrand';
import type { Agent, Artifact, Skill, SkillSession, SessionEvent, BrandListItem, BrandGraph } from '../types/cowork';

// ─────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────

/** Brand OS sections as their own pages (sidebar entries) → the section the page opens on. */
const BRAND_SECTION_VIEWS: Record<string, 'pulse' | 'brand' | 'promotables' | 'audiences' | 'competitors' | 'library'> = { 'brand-pulse': 'pulse', 'brand-id': 'brand', 'brand-promotables': 'promotables', 'brand-audiences': 'audiences', 'brand-competitors': 'competitors', 'brand-library': 'library' };

type CoworkView =
  | 'onboarding'
  | 'home'
  | 'chats'
  | 'inbox'
  | 'marketplace'
  | 'agents'
  | 'agent-detail'
  | 'agent-chat'
  | 'session'
  | 'artifact'
  | 'brand-os'
  | 'brand-pulse'
  | 'brand-id'
  | 'brand-promotables'
  | 'brand-audiences'
  | 'brand-competitors'
  | 'brand-library'
  | 'brand-wiki'
  | 'tools'
  | 'growth-diary'
  | 'google-ads'
  | 'settings'
  | 'call'
  | 'tasks'
  | 'okrs'
  | 'dashboards'
  | 'files'
  | 'library'
  | 'integrations'
  | 'projects'
  | 'project-detail'
  | 'approvals'
  | 'campaigns'
  | 'usage';

// ─────────────────────────────────────────────
//  Main Component
// ─────────────────────────────────────────────

// ─── Google Ads local session type (stored in localStorage) ──────────────────
interface GoogleAdsLocalSession {
  id: string;
  title: string;
  createdAt: string;
}

const GA_STORAGE_KEY = 'google-ads-sessions';

function loadGoogleAdsSessions(): GoogleAdsLocalSession[] {
  try {
    const raw = JSON.parse(localStorage.getItem(GA_STORAGE_KEY) ?? '[]') as { id: string; title: string; createdAt: string }[];
    return raw.map(s => ({ id: s.id, title: s.title, createdAt: s.createdAt }));
  } catch { return []; }
}

const ONBOARDING_DONE_KEY = 'cowork_onboarding_done';

function fireConfetti() {
  const duration = 2000;
  const end = Date.now() + duration;
  const colors = ['#be43ff', '#a9f600', '#6366f1', '#f59e0b', '#0ea5e9'];

  (function frame() {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

export function CoworkPage({
  initialView,
  embedded = false,
}: { initialView?: CoworkView; embedded?: boolean } = {}) {
  const [view, setView] = useState<CoworkView>(initialView || 'home');

  // Embedded in the Beatly shell, the left nav is Beatly's own sidebar and the
  // URL is the source of truth — follow `initialView` when the route changes.
  useEffect(() => {
    if (embedded && initialView) setView(initialView);
  }, [embedded, initialView]);

  // Brand selection also lives outside this page when embedded (agent rail,
  // Brand OS). Follow it, so the rail can't be discussing one brand while this
  // page edits another.
  useEffect(() => {
    const sync = () => {
      const id = localStorage.getItem('cowork_active_brand_id') || undefined;
      setActiveBrandId((prev) => (id !== prev ? id : prev));
    };
    window.addEventListener('cowork-active-brand-changed', sync);
    return () => window.removeEventListener('cowork-active-brand-changed', sync);
  }, []);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [pendingNewTask, setPendingNewTask] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [pinnedProjects, setPinnedProjects] = useState<PinnedProject[]>([]);
  const [connectedIntegrations, setConnectedIntegrations] = useState<Array<{ provider: string; label: string }>>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [skillsByAgent, setSkillsByAgent] = useState<Record<string, Skill[]>>({});
  const [sessions, setSessions] = useState<SkillSession[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [pendingMessage, setPendingMessage] = useState('');
  const [activeSession, setActiveSession] = useState<SkillSession | null>(null);
  const [activeArtifact, setActiveArtifact] = useState<Artifact | null>(null);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [briefDrawerOpen, setBriefDrawerOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Notifications
  const { notifications: notifList, unreadCount: notifUnread, markRead: notifMarkRead, markAllRead: notifMarkAllRead } = useNotifications();

  // Content queue drafts for sidebar
  const [contentDrafts, setContentDrafts] = useState<Array<{ id: string; platform: string; title: string; status: string }>>([]);
  const [activeContentId, setActiveContentId] = useState<string | null>(null);

  // Google Ads sessions (localStorage)
  const [googleAdsSessions, setGoogleAdsSessions] = useState<GoogleAdsLocalSession[]>(loadGoogleAdsSessions);
  const [activeGoogleAdsSessionId, setActiveGoogleAdsSessionId] = useState<string | null>(null);

  // Brand selection state
  const [availableBrands, setAvailableBrands] = useState<BrandListItem[]>([]);
  const [activeBrandId, setActiveBrandId] = useState<string | undefined>(
    localStorage.getItem('cowork_active_brand_id') || undefined
  );
  // No brand chosen, or one that is not this client's (another client's, or none from the back office's "All
  // brands"): use the shared resolution — the client's main brand — so no page ever edits the placeholder brand.
  const sharedBrand = useActiveBrand();
  useEffect(() => {
    if (!sharedBrand.brandId || sharedBrand.brands.length === 0) return;
    if (activeBrandId && sharedBrand.brands.some((b) => b.brandId === activeBrandId)) return;
    setActiveBrandId(sharedBrand.brandId);
  }, [sharedBrand.brandId, sharedBrand.brands, activeBrandId]);
  // Brand pages wait for a real brand rather than flashing (and fetching) the placeholder one first.
  const brandReady = !!activeBrandId || (sharedBrand.loaded && sharedBrand.brands.length === 0);

  // Brand switch handler — auto-seeds brand data on first access
  const handleBrandChange = useCallback(async (brandId: string) => {
    setActiveBrandId(brandId);
    localStorage.setItem('cowork_active_brand_id', brandId);
    const brand = availableBrands.find(b => b.brandId === brandId);
    if (!brand) return;

    // Check if this brand has data in cowork backend
    const existing = await coworkApi.brands.get(brandId).catch(() => null);
    const hasAssets = existing?.brand?.logos?.logoDark
      || existing?.brand?.logos?.logoLight
      || (existing?.brand?.primaryColors && existing.brand.primaryColors.length > 0)
      || (existing?.brand?.fonts && existing.brand.fonts.length > 0);

    if (!existing?.brand?.name && brand.brandName) {
      // First time — seed basic info
      console.log('[cowork] Auto-seeding brand:', brand.brandName);
      await coworkApi.brands.update({
        brand: {
          name: brand.brandName,
          description: '',
          website: brand.brandUrl || '',
          industry: '',
          usp: '',
          voiceTone: '',
          voiceAttributes: [],
          writingStyle: '',
          thingsToAvoid: [],
          keyMessages: [],
          targetStatement: '',
          primaryColors: [],
          fonts: [],
          logos: brand.brandLogoUrl ? { logoDark: brand.brandLogoUrl } : undefined,
        },
      } as Partial<BrandGraph>, brandId).catch(err => console.error('[cowork] Failed to seed brand:', err));
    }

    // Auto-fetch from Brandfetch if no assets exist and brand has a URL
    if (!hasAssets && brand.brandUrl) {
      console.log('[cowork] Auto-fetching Brandfetch data for:', brand.brandUrl);
      coworkApi.brands.brandfetch(brand.brandUrl, brandId)
        .then(result => {
          if (result.success) {
            console.log('[cowork] Brandfetch auto-populated:', brand.brandName);
          }
        })
        .catch(() => { /* Brandfetch is optional */ });
    }
  }, [availableBrands]);

  // Plan / credits state
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Call state
  const [callInviteOpen, setCallInviteOpen] = useState(false);
  const [groupChatInviteOpen, setGroupChatInviteOpen] = useState(false);
  const [callAgentIds, setCallAgentIds] = useState<string[]>([]);
  const [callInitialAgent, setCallInitialAgent] = useState<string | null>(null);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);

  // Load agents + sessions on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [agentList, sessionData, artifactData, contentData] = await Promise.all([
          coworkApi.agents.list(),
          coworkApi.sessions.list().catch(() => ({ sessions: [], total: 0 })),
          coworkApi.artifacts.list().catch(() => ({ artifacts: [] })),
          coworkApi.contentQueue.list().catch(() => ({ items: [] })),
        ]);
        setAgents(agentList);
        setSessions(sessionData.sessions);
        setArtifacts(artifactData.artifacts);
        setContentDrafts(contentData.items.map(i => ({ id: i.id, platform: i.platform, title: i.title, status: i.status })));

        // Load skills for all agents in the background (single call, grouped by agent)
        coworkApi.skills.list()
          .then(allSkills => {
            const byAgent: Record<string, Skill[]> = {};
            agentList.forEach(a => { byAgent[a.id] = []; });
            allSkills.forEach(s => { (byAgent[s.agentId] ||= []).push(s); });
            setSkillsByAgent(byAgent);
          })
          .catch(console.error);

        // Load available brands from Team Brands API in the background
        (async () => {
          const token = getTokenFromStorage();
          const user = getUserFromStorage();
          const teamId = user?.team?.team_id;
          if (token && teamId) {
            const teamBrands = await getTeamBrands(teamId, token).catch(() => []);
            console.log('[cowork] Team brands:', teamBrands);

            const brands: BrandListItem[] = teamBrands.map(b => ({
              brandId: b.brand_id,
              brandName: b.name,
              brandLogoUrl: b.logo_url || undefined,
              brandUrl: undefined,
              hasRichData: false,
            }));

            setAvailableBrands(brands);

            if (brands.length >= 1) {
              // Respect saved brand selection. With none saved, fall back the
              // same way the header badge does — the brand of the most recent
              // campaign — not to whichever brand the team happens to list
              // first. This value is persisted below, so picking wrong here
              // locks the wrong brand in permanently, and Brand OS and the wiki
              // would then disagree with the badge the user is looking at.
              const savedBrandId = localStorage.getItem('cowork_active_brand_id');
              const derivedDefault = savedBrandId
                ? null
                : await coworkApi.brands.list()
                    .then(r => r.defaultBrandId ?? null)
                    .catch(() => null);
              const initialBrand = savedBrandId && brands.some(b => b.brandId === savedBrandId)
                ? savedBrandId
                : derivedDefault && brands.some(b => b.brandId === derivedDefault)
                  ? derivedDefault
                  : brands[0].brandId;
              setActiveBrandId(initialBrand);
              localStorage.setItem('cowork_active_brand_id', initialBrand);
              // Auto-seed selected brand in cowork backend
              const existing = await coworkApi.brands.get(initialBrand).catch(() => null);
              if (!existing?.brand?.name) {
                const tb = teamBrands.find(b => b.brand_id === initialBrand) || teamBrands[0];
                await coworkApi.brands.update({
                  brand: {
                    name: tb.name,
                    description: '',
                    website: '',
                    industry: '',
                    usp: '',
                    voiceTone: '',
                    voiceAttributes: [],
                    writingStyle: '',
                    thingsToAvoid: [],
                    keyMessages: [],
                    targetStatement: '',
                    primaryColors: [],
                    fonts: [],
                    logos: tb.logo_url ? { logoDark: tb.logo_url } : undefined,
                  },
                } as Partial<BrandGraph>, initialBrand).catch(() => {});
              }
            }

          }
        })().catch(() => { /* if brand check fails, proceed to marketplace */ });

        // First-time user — show onboarding wizard (independent of team/brand state). Never inside the back office:
        // there the frame IS the page the CM opened, and the wizard is the client's welcome, not theirs.
        if (!localStorage.getItem(ONBOARDING_DONE_KEY) && !isEmbedMode()) {
          setView('onboarding');
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? `Could not connect to Cowork API. Is the backend running? (${err.message})`
            : 'Unknown error loading agents'
        );
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Fetch plan / credit info on mount
  useEffect(() => {
    coworkApi.stripe.getPlan()
      .then(setPlanInfo)
      .catch(() => { /* plan endpoint may not exist yet */ });
  }, []);

  // Pinned projects for the sidebar
  const refreshPinnedProjects = useCallback(() => {
    if (!brandReady) return; // the brand is still being resolved: list once, for the right one
    coworkApi.projects.list({ brandId: activeBrandId, pinnedOnly: true })
      .then((d) => setPinnedProjects(d.projects.map((p) => ({ id: p.id, title: p.title, emoji: p.emoji }))))
      .catch(() => setPinnedProjects([]));
  }, [activeBrandId, brandReady]);
  useEffect(() => { refreshPinnedProjects(); }, [refreshPinnedProjects]);

  // Connected integrations for the channels submenu
  useEffect(() => {
    coworkApi.integrations.list()
      .then((d) => setConnectedIntegrations(
        (d.integrations || []).map((i: { provider: string; profileName?: string }) => ({
          provider: i.provider,
          label: i.profileName ? `${i.provider} · ${i.profileName}` : i.provider,
        })),
      ))
      .catch(() => setConnectedIntegrations([]));
  }, []);

  // Handle GA4 OAuth redirect — show property picker
  const [ga4Properties, setGa4Properties] = useState<Array<{ propertyId: string; displayName: string; accountName: string }>>([]);
  const [showGa4Picker, setShowGa4Picker] = useState(false);
  const [showGa4Success, setShowGa4Success] = useState<string | null>(null);
  const [ga4BrandId, setGa4BrandId] = useState<string>('');

  // Handle Google Ads OAuth redirect — show account picker
  const [gadsAccounts, setGadsAccounts] = useState<Array<{ customerId: string; descriptiveName: string }>>([]);
  const [showGadsPicker, setShowGadsPicker] = useState(false);
  const [showGadsSuccess, setShowGadsSuccess] = useState<string | null>(null);

  // Handle LinkedIn ad account picker after OAuth
  const [liAdAccounts, setLiAdAccounts] = useState<Array<{ adAccountId: string; name: string; status: string; currency: string }>>([]);
  const [showLiAdPicker, setShowLiAdPicker] = useState(false);
  const [showLiAdSuccess, setShowLiAdSuccess] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // LinkedIn connect success — offer ad account selection or show success
    if (params.get('integration_success') === 'linkedin') {
      window.history.replaceState({}, '', window.location.pathname);
      coworkApi.integrations.linkedinAdAccounts()
        .then(data => {
          if (data.accounts.length === 1) {
            const a = data.accounts[0];
            coworkApi.integrations.linkedinSelectAdAccount(a.adAccountId, a.name)
              .then(() => { setShowLiAdSuccess(a.name); fireConfetti(); });
          } else if (data.accounts.length > 1) {
            setLiAdAccounts(data.accounts);
            setShowLiAdPicker(true);
          } else {
            // No ad accounts — still show success for posting connection
            setShowLiAdSuccess('LinkedIn Profile');
            fireConfetti();
          }
        })
        .catch(() => {
          // API error — still show success for basic connection
          setShowLiAdSuccess('LinkedIn Profile');
          fireConfetti();
        });
    }
    if (params.get('gads_connected') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      coworkApi.integrations.googleAdsAccounts()
        .then(data => {
          if (data.accounts.length === 1) {
            const a = data.accounts[0];
            coworkApi.integrations.googleAdsSelectAccount(a.customerId, a.descriptiveName)
              .then(() => { setShowGadsSuccess(a.descriptiveName); fireConfetti(); });
          } else if (data.accounts.length > 1) {
            setGadsAccounts(data.accounts);
            setShowGadsPicker(true);
          } else {
            alert('Google Ads connected but no accounts found. Make sure your Google account has access to a Google Ads account.');
          }
        })
        .catch(err => {
          console.error('[google-ads] Failed to load accounts:', err);
          alert('Google Ads connected but failed to load accounts. Try again from Settings.');
        });
    }
    if (params.get('ga4_connected') === 'true') {
      const brandId = params.get('brandId') || activeBrandId;
      setGa4BrandId(brandId || '');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      // Fetch available properties
      coworkApi.ga4.properties(brandId || '')
        .then(data => {
          if (data.properties.length === 1) {
            // Auto-select if only one property
            const p = data.properties[0];
            coworkApi.ga4.selectProperty(brandId || '', p.propertyId, p.displayName)
              .then(() => { setShowGa4Success(p.displayName); fireConfetti(); });
          } else if (data.properties.length > 1) {
            setGa4Properties(data.properties);
            setShowGa4Picker(true);
          } else {
            alert('GA4 connected but no properties found. Make sure your Google account has GA4 properties.');
          }
        })
        .catch(err => {
          console.error('[ga4] Failed to load properties:', err);
          alert('GA4 connected but failed to load properties. Try again from Settings.');
        });
    }
  }, []);

  const handleGa4PropertySelect = (propertyId: string, displayName: string) => {
    coworkApi.ga4.selectProperty(ga4BrandId, propertyId, displayName)
      .then(() => {
        setShowGa4Picker(false);
        setShowGa4Success(displayName);
        fireConfetti();
      });
  };

  // Auto-select Account Manager (ACMO) as default agent when agents load
  useEffect(() => {
    if (agents.length > 0 && !selectedAgent) {
      const acmo = agents.find(a => a.id === 'account-manager');
      if (acmo) {
        setSelectedAgent(acmo);
      } else {
        // Fallback: if account-manager doesn't exist, show marketplace
        setView('home');
      }
    }
  }, [agents, selectedAgent]);

  const handleChatWithAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setView('agent-chat');
  };

  /** Direct 1:1 call — skips invite panel */
  const handleCallAgent = (agent: Agent) => {
    setCallAgentIds([agent.id]);
    setView('call');
  };

  /** Group call — opens invite panel to pick multiple agents */
  const handleGroupCall = () => {
    setCallInitialAgent(null);
    setCallInviteOpen(true);
  };

  /** Group chat — opens invite panel then starts chat with selected agents */
  const handleGroupChat = () => {
    setCallInitialAgent(null);
    setGroupChatInviteOpen(true);
  };

  const handleStartCall = (agentIds: string[]) => {
    setCallInviteOpen(false);
    setCallAgentIds(agentIds);
    setView('call');
  };

  const handleStartGroupChat = (agentIds: string[]) => {
    setGroupChatInviteOpen(false);
    // For group chat, start a call-mode chat (multi-agent) but without auto-voice
    setCallAgentIds(agentIds);
    setView('call');
  };

  const handleCallEnded = () => {
    setCallAgentIds([]);
    setView('home');
  };

  const refreshContentDrafts = () => {
    coworkApi.contentQueue.list().then(data => {
      setContentDrafts(data.items.map(i => ({ id: i.id, platform: i.platform, title: i.title, status: i.status })));
    }).catch(() => {});
  };

  const handleRunSkill = (skill: Skill) => {
    setSelectedSkill(skill);
    setBriefDrawerOpen(true);
  };

  const handleBriefSubmit = async (briefData: Record<string, unknown>) => {
    if (!selectedSkill) return;
    setBriefDrawerOpen(false);
    try {
      const { sessionId } = await coworkApi.skills.run(selectedSkill.id, briefData, activeBrandId);
      const session = await coworkApi.sessions.get(sessionId);
      setActiveSession(session);
      setSessions(prev => [session, ...prev]);
      setView('session');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start skill');
    }
  };

  const handleSessionClick = async (session: SkillSession) => {
    setActiveSession(session);
    setView('session');
  };

  const handleNewTask = () => {
    setSelectedAgent(null);
    setActiveSession(null);
    setActiveArtifact(null);
    setPendingNewTask(true);
    setView('tasks');
  };

  const handleArtifactClick = async (artifact: Artifact) => {
    // Fetch full artifact (with HTML) if we only have the stub
    if (!artifact.html) {
      const full = await coworkApi.artifacts.get(artifact.id);
      setActiveArtifact(full);
    } else {
      setActiveArtifact(artifact);
    }
    setView('artifact');
  };

  const handleArtifactGenerated = async (artifactId: string) => {
    const full = await coworkApi.artifacts.get(artifactId);
    setArtifacts(prev => [full, ...prev.filter(a => a.id !== artifactId)]);
    setActiveArtifact(full);
    setView('artifact');
  };

  const handleArtifactDeleted = (artifactId: string) => {
    setArtifacts(prev => prev.filter(a => a.id !== artifactId));
    if (activeArtifact?.id === artifactId) {
      setActiveArtifact(null);
      setView('home');
    }
  };

  const agentForSession = activeSession
    ? agents.find(a => a.id === activeSession.agentId) ?? null
    : null;

  const skillForSession = activeSession
    ? Object.values(skillsByAgent).flat().find(s => s.id === activeSession.skillId) ?? null
    : null;

  return (
    <div className="flex h-[calc(100vh-3.5rem)] bg-[#f8f9fa]">
      {/* ── Left Sidebar — hidden during onboarding, and when embedded in the
           Beatly shell (AppSidebar carries these entries there instead) ── */}
      {view !== 'onboarding' && !embedded && <OmniSidebar
        currentView={view as SidebarView}
        onNavigate={(v) => setView(v as CoworkView)}
        onNewTask={handleNewTask}
        onProjectOpen={(id) => { setSelectedProjectId(id); setView('project-detail'); }}
        pinnedProjects={pinnedProjects}
        availableBrands={availableBrands}
        activeBrandId={activeBrandId}
        onBrandChange={handleBrandChange}
        planInfo={planInfo}
        onUpgradeClick={() => setUpgradeModalOpen(true)}
        connectedIntegrations={connectedIntegrations}
      />}

      {/* ── Main Area ── */}
      <div className={cn(
        'flex-1 flex flex-col min-w-0 relative bg-[#f8f9fa] overflow-hidden',
        !embedded && 'rounded-tl-[24px]',
      )}>
        {/* Concave corner — the Beatly shell draws its own when embedded */}
        {!embedded && (
          <div className="absolute top-0 left-0 z-10 pointer-events-none" style={{ width: 24, height: 24 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M24 0H0V24C0 10.745 10.745 0 24 0Z" fill="white" />
            </svg>
          </div>
        )}

        {loading && <LoadingState />}
        {!loading && error && <ErrorState message={error} />}
        {!loading && !error && view === 'onboarding' && (
          <CoworkOnboarding
            agents={agents}
            onComplete={() => { localStorage.setItem(ONBOARDING_DONE_KEY, 'true'); setView('home'); }}
            onSkip={() => { localStorage.setItem(ONBOARDING_DONE_KEY, 'true'); setView('home'); }}
          />
        )}
        {!loading && !error && view === 'home' && (
          <CoworkHomePage
            onNavigateToChat={(agentId, message) => {
              const a = agents.find(ag => ag.id === agentId);
              if (a) { setPendingMessage(message || ''); setSelectedAgent(a); setView('agent-chat'); }
            }}
            onNavigateToView={(v) => {
              // Pinned-project tiles encode "project-detail:<id>"
              if (v.startsWith('project-detail:')) {
                setSelectedProjectId(v.slice('project-detail:'.length));
                setView('project-detail');
                return;
              }
              setView(v as CoworkView);
            }}
            brandId={activeBrandId}
          />
        )}
        {!loading && !error && (view === 'marketplace' || view === 'agents') && (
          <MarketplaceView
            agents={agents}
            skillsByAgent={skillsByAgent}
            onChatWithAgent={handleChatWithAgent}
            onCallAgent={handleCallAgent}
            onGroupCall={handleGroupCall}
            onGroupChat={handleGroupChat}
            onRunSkill={(skill, agent) => { setSelectedAgent(agent); handleRunSkill(skill); }}
            onEditAgent={setEditingAgent}
          />
        )}
        {!loading && !error && view === 'agent-chat' && selectedAgent && (
          <AgentChatPage
            key={selectedAgent.id}
            agent={selectedAgent}
            onBack={() => { setPendingMessage(''); setView('home'); }}
            onContentSaved={refreshContentDrafts}
            onNavigateToTasks={(taskId) => { setView('tasks'); if (taskId) setSelectedTaskId(taskId); }}
            onViewDashboard={() => setView('dashboards')}
            brandId={activeBrandId}
            initialMessage={pendingMessage}
          />
        )}
        {!loading && !error && view === 'session' && activeSession && (
          <SessionView
            key={activeSession.id}
            session={activeSession}
            agent={agentForSession}
            skill={skillForSession}
            onSessionUpdate={setActiveSession}
            onArtifactReady={handleArtifactGenerated}
          />
        )}
        {!loading && !error && view === 'artifact' && activeArtifact && (
          <ArtifactView
            artifact={activeArtifact}
            agents={agents}
            onBack={() => setView('home')}
            onDeleted={handleArtifactDeleted}
          />
        )}
        {!loading && !error && brandReady && view === 'brand-wiki' && (
          <BrandWikiPage onBack={() => setView('home')} brandId={activeBrandId} />
        )}
        {!loading && !error && brandReady && view === 'brand-os' && (
          <BrandOSPage onBack={() => setView('home')} brandId={activeBrandId} />
        )}
        {!loading && !error && brandReady && BRAND_SECTION_VIEWS[view] && (
          <BrandOSPage key={view} onBack={() => setView('home')} brandId={activeBrandId} section={BRAND_SECTION_VIEWS[view]} standalone />
        )}
        {view === 'chats' && <ChatPage />}
        {view === 'tools' && (
          <ToolsPage onBack={() => setView('home')} />
        )}
        {view === 'growth-diary' && (
          <GrowthDiaryPage agents={agents} onBack={() => setView('home')} />
        )}
        {view === 'settings' && <AccountSettingsPage userName="" />}
        {view === 'tasks' && (
          <TaskBoardPage
            agents={agents}
            onBack={() => setView('home')}
            brandId={activeBrandId}
            initialTaskId={selectedTaskId || undefined}
            onTaskViewed={() => setSelectedTaskId(null)}
            initialShowCreate={pendingNewTask}
            onCreateConsumed={() => setPendingNewTask(false)}
          />
        )}
        {view === 'okrs' && (
          <OKRPage
            agents={agents}
            brandId={activeBrandId}
            onBack={() => setView('home')}
          />
        )}
        {brandReady && view === 'dashboards' && (
          <DashboardsPage
            agents={agents}
            brandId={activeBrandId || 'default'}
            onBack={() => setView('home')}
          />
        )}
        {brandReady && view === 'files' && (
          <FilesPage
            brandId={activeBrandId || 'default'}
            onBack={() => setView('home')}
          />
        )}
        {brandReady && view === 'library' && (
          <LibraryPage
            brandId={activeBrandId || 'default'}
            onBack={() => setView('home')}
          />
        )}
        {view === 'integrations' && (
          <IntegrationsPage
            onOpenGoogleAds={() => setView('google-ads')}
          />
        )}
        {view === 'projects' && (
          <ProjectsPage
            brandId={activeBrandId}
            onOpenProject={(id) => { setSelectedProjectId(id); setView('project-detail'); refreshPinnedProjects(); }}
          />
        )}
        {view === 'project-detail' && selectedProjectId && (
          <ProjectDetailPage
            projectId={selectedProjectId}
            onBack={() => { setView('projects'); refreshPinnedProjects(); }}
            onOpenTasks={() => setView('tasks')}
            onOpenOkrs={() => setView('okrs')}
            onOpenDashboards={() => setView('dashboards')}
          />
        )}
        {brandReady && view === 'approvals' && (
          <ApprovalsPage
            brandId={activeBrandId || 'default'}
            onBack={() => setView('home')}
          />
        )}

        {view === 'campaigns' && (
          <CampaignRoomPage onBack={() => setView('home')} />
        )}
        {view === 'usage' && (
          <UsagePage
            onBack={() => setView('home')}
          />
        )}
        {view === 'google-ads' && (
          <GoogleAdsAgentPage
            onBack={() => setView('home')}
            initialSessionId={activeGoogleAdsSessionId ?? undefined}
            onSessionSaved={(s) => {
              setGoogleAdsSessions(prev => {
                const next = prev.find(x => x.id === s.id)
                  ? prev.map(x => x.id === s.id ? { id: s.id, title: s.title, createdAt: s.createdAt } : x)
                  : [{ id: s.id, title: s.title, createdAt: s.createdAt }, ...prev];
                return next;
              });
              setActiveGoogleAdsSessionId(s.id);
            }}
            onReportCreated={async (artifactId) => {
              try {
                const full = await coworkApi.artifacts.get(artifactId);
                setArtifacts(prev => [full, ...prev.filter(a => a.id !== artifactId)]);
              } catch { /* best-effort */ }
            }}
          />
        )}
        {view === 'call' && callAgentIds.length > 0 && (() => {
          const callAgentObjects = callAgentIds.map(id => agents.find(a => a.id === id)).filter(Boolean) as Agent[];
          const primaryAgent = callAgentObjects[0];
          if (!primaryAgent) return null;
          return (
            <AgentChatPage
              agent={primaryAgent}
              onBack={handleCallEnded}
              onContentSaved={refreshContentDrafts}
              onNavigateToTasks={(taskId) => { setView('tasks'); if (taskId) setSelectedTaskId(taskId); }}
              onViewDashboard={() => setView('dashboards')}
              callAgents={callAgentObjects}
              autoVoice
              voiceCall
              brandId={activeBrandId}
            />
          );
        })()}
      </div>

      {/* ── Call invite panel ── */}
      {callInviteOpen && (
        <CallInvitePanel
          agents={agents}
          initialSelected={callInitialAgent ? [callInitialAgent] : []}
          onStart={handleStartCall}
          onClose={() => setCallInviteOpen(false)}
        />
      )}

      {groupChatInviteOpen && (
        <CallInvitePanel
          agents={agents}
          initialSelected={[]}
          onStart={handleStartGroupChat}
          onClose={() => setGroupChatInviteOpen(false)}
          title="Start Group Chat"
          actionLabel="Start Chat"
        />
      )}

      {/* ── Brief Drawer ── */}
      {selectedSkill && (
        <SkillBriefDrawer
          open={briefDrawerOpen}
          skill={selectedSkill}
          agent={agents.find(a => a.id === selectedSkill.agentId) ?? null}
          onClose={() => setBriefDrawerOpen(false)}
          onSubmit={handleBriefSubmit}
        />
      )}

      {/* Content Detail Modal */}
      {activeContentId && (
        <ContentDetailModal
          contentId={activeContentId}
          onClose={() => setActiveContentId(null)}
          onUpdated={() => {
            refreshContentDrafts();
          }}
        />
      )}

      {/* Agent Edit Modal */}
      {editingAgent && (
        <AgentEditModal
          agent={editingAgent}
          onClose={() => setEditingAgent(null)}
          onSaved={async () => {
            const refreshed = await coworkApi.agents.list();
            setAgents(refreshed);
            // Update the editing agent reference with fresh data
            const updated = refreshed.find(a => a.id === editingAgent.id);
            if (updated) setEditingAgent(updated);
          }}
        />
      )}

      {/* GA4 Property Picker Dialog — follows DevMode modal design */}
      {showGa4Picker && ga4Properties.length > 0 && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowGa4Picker(false)} />
          <div
            className="relative bg-white rounded-[24px] w-full max-w-[480px] overflow-hidden"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowGa4Picker(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-lg flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-gray-100 transition-all"
            >
              <span className="text-lg">&times;</span>
            </button>

            <div className="p-8 pt-8 text-center">
              {/* Logo + GA4 icon */}
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#A9F600', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <svg width="28" height="28" viewBox="0 0 502 502" fill="#2E0854" xmlns="http://www.w3.org/2000/svg">
                    <path d="M261.8 22.8093C250.299 22.8093 239.079 23.6508 228.139 25.0534C227.858 25.0534 227.578 25.0534 227.578 25.0534C222.529 25.8949 218.602 30.383 218.602 35.7125C218.602 40.7616 218.602 163.903 218.602 163.903C218.602 169.794 223.37 174.843 229.541 174.843H262.08C310.046 174.843 349.037 213.833 349.037 261.799C349.037 309.766 310.046 348.756 262.08 348.756C214.114 348.756 175.124 309.766 175.124 261.799V229.261C175.124 223.37 170.355 218.321 164.184 218.321H35.9931C30.6635 218.321 26.1754 222.248 25.3339 227.297C25.3339 227.578 25.3339 227.858 25.3339 228.139C23.6509 239.079 23.0898 250.299 23.0898 261.799C23.0898 393.917 130.243 501.07 262.361 501.07C394.478 501.07 501.631 393.917 501.631 261.799C501.631 129.682 393.917 22.8093 261.8 22.8093Z" />
                    <path d="M33.1877 155.488C48.0545 167.55 67.1288 174.843 87.8862 174.843H163.903C169.794 174.843 174.843 170.074 174.843 163.903V87.8864C174.843 67.1291 167.55 48.0547 155.488 33.188C153.524 30.944 151.561 28.6999 149.317 26.4559C133.608 10.7476 111.729 0.929932 87.8862 0.929932C40.2004 0.929932 0.929688 39.9201 0.929688 87.8864C0.929688 112.01 10.7474 133.609 26.4556 149.317C28.4192 151.561 30.6632 153.525 33.1877 155.488Z" />
                  </svg>
                </div>
                <div className="w-6 h-px bg-gray-200 relative">
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[10px] text-[#98a2b3]">+</div>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-[#fff3e0] flex items-center justify-center" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 18V6h2v12H4zm5 0V9h2v9H9zm5 0v-6h2v6h-2zm5 0V4h2v14h-2z" fill="#E37400"/></svg>
                </div>
              </div>

              <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
                Connect Google Analytics
              </h3>
              <p className="text-[#667085] text-sm mb-6 max-w-[340px] mx-auto">
                Select which GA4 property to connect for this brand. Your agents will use this data for performance tracking.
              </p>

              {/* Property list */}
              <div className="space-y-2 max-h-[280px] overflow-y-auto text-left px-1">
                {ga4Properties.map(p => (
                  <button
                    key={p.propertyId}
                    onClick={() => handleGa4PropertySelect(p.propertyId, p.displayName)}
                    className="w-full text-left p-4 rounded-2xl border border-gray-100 hover:border-[#be43ff]/30 hover:bg-[#faf5ff] transition-all group"
                    style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#fff3e0] flex items-center justify-center shrink-0">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 18V6h2v12H4zm5 0V9h2v9H9zm5 0v-6h2v6h-2zm5 0V4h2v14h-2z" fill="#E37400"/></svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] text-[#101828] truncate" style={{ fontWeight: 500 }}>{p.displayName}</div>
                        <div className="text-[12px] text-[#667085]">{p.accountName} &middot; {p.propertyId}</div>
                      </div>
                      <div className="shrink-0 w-6 h-6 rounded-full border-2 border-gray-200 group-hover:border-[#be43ff] flex items-center justify-center transition-colors">
                        <div className="w-2.5 h-2.5 rounded-full bg-transparent group-hover:bg-[#be43ff] transition-colors" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Skip */}
              <button
                onClick={() => setShowGa4Picker(false)}
                className="mt-5 px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-[#344054] hover:bg-gray-50 transition-all active:scale-[0.97]"
                style={{ fontWeight: 500 }}
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal (triggered by 402 or manual open) */}
      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        planInfo={planInfo}
      />

      {/* Google Ads Account Picker Modal */}
      {showGadsPicker && gadsAccounts.length > 0 && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowGadsPicker(false)} />
          <div
            className="relative bg-white rounded-[24px] w-full max-w-[480px] overflow-hidden"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
          >
            <button
              onClick={() => setShowGadsPicker(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-lg flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-gray-100 transition-all"
            >
              <span className="text-lg">&times;</span>
            </button>
            <div className="p-8 pt-8 text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#e8f0fe] flex items-center justify-center mx-auto mb-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <span className="text-[24px]">📢</span>
              </div>
              <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
                Select Google Ads Account
              </h3>
              <p className="text-[#667085] text-sm mb-6 max-w-[340px] mx-auto">
                Choose which Google Ads account your agents should manage.
              </p>
              <div className="space-y-2 max-h-[280px] overflow-y-auto text-left px-1">
                {gadsAccounts.map(a => (
                  <button
                    key={a.customerId}
                    onClick={() => {
                      coworkApi.integrations.googleAdsSelectAccount(a.customerId, a.descriptiveName)
                        .then(() => {
                          setShowGadsPicker(false);
                          setShowGadsSuccess(a.descriptiveName);
                          fireConfetti();
                        });
                    }}
                    className="w-full text-left p-4 rounded-2xl border border-gray-100 hover:border-[#4285F4]/30 hover:bg-[#4285F4]/5 transition-all group"
                    style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#e8f0fe] flex items-center justify-center shrink-0">
                        <span className="text-[16px]">📢</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] text-[#101828] truncate" style={{ fontWeight: 500 }}>{a.descriptiveName}</div>
                        <div className="text-[12px] text-[#667085]">ID: {a.customerId}</div>
                      </div>
                      <div className="shrink-0 w-6 h-6 rounded-full border-2 border-gray-200 group-hover:border-[#4285F4] flex items-center justify-center transition-colors">
                        <div className="w-2.5 h-2.5 rounded-full bg-transparent group-hover:bg-[#4285F4] transition-colors" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowGadsPicker(false)}
                className="mt-5 px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-[#344054] hover:bg-gray-50 transition-all active:scale-[0.97]"
                style={{ fontWeight: 500 }}
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Ads Success Modal */}
      {showGadsSuccess && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowGadsSuccess(null)} />
          <div
            className="relative bg-white rounded-[24px] w-full max-w-[400px] overflow-hidden"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
          >
            <div className="p-8 pt-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
                Google Ads Connected
              </h3>
              <p className="text-[#667085] text-sm mb-1">
                <span style={{ fontWeight: 500 }}>{showGadsSuccess}</span>
              </p>
              <p className="text-[#667085] text-sm mb-6 max-w-[300px] mx-auto">
                Your agents can now manage campaigns, budgets, and keywords in this account.
              </p>
              <button
                onClick={() => setShowGadsSuccess(null)}
                className="px-8 py-2.5 rounded-xl bg-[#be43ff] text-white text-sm hover:bg-[#a830e0] transition-all active:scale-[0.97]"
                style={{ fontWeight: 600 }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LinkedIn Ad Account Picker Modal */}
      {showLiAdPicker && liAdAccounts.length > 0 && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowLiAdPicker(false)} />
          <div
            className="relative bg-white rounded-[24px] w-full max-w-[480px] overflow-hidden"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
          >
            <button
              onClick={() => setShowLiAdPicker(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-lg flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-gray-100 transition-all"
            >
              <span className="text-lg">&times;</span>
            </button>
            <div className="p-8 pt-8 text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#e8f4f8] flex items-center justify-center mx-auto mb-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <span className="text-[20px] font-bold text-[#0a66c2]">in</span>
              </div>
              <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
                Select LinkedIn Ad Account
              </h3>
              <p className="text-[#667085] text-sm mb-6 max-w-[340px] mx-auto">
                Choose which LinkedIn Ad Account your agents should manage.
              </p>
              <div className="space-y-2 max-h-[280px] overflow-y-auto text-left px-1">
                {liAdAccounts.map(a => (
                  <button
                    key={a.adAccountId}
                    onClick={() => {
                      coworkApi.integrations.linkedinSelectAdAccount(a.adAccountId, a.name)
                        .then(() => {
                          setShowLiAdPicker(false);
                          setShowLiAdSuccess(a.name);
                          fireConfetti();
                        });
                    }}
                    className="w-full text-left p-4 rounded-2xl border border-gray-100 hover:border-[#0a66c2]/30 hover:bg-[#0a66c2]/5 transition-all group"
                    style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#e8f4f8] flex items-center justify-center shrink-0">
                        <span className="text-[14px] font-bold text-[#0a66c2]">in</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] text-[#101828] truncate" style={{ fontWeight: 500 }}>{a.name}</div>
                        <div className="text-[12px] text-[#667085]">ID: {a.adAccountId} &middot; {a.currency}</div>
                      </div>
                      <div className="shrink-0 w-6 h-6 rounded-full border-2 border-gray-200 group-hover:border-[#0a66c2] flex items-center justify-center transition-colors">
                        <div className="w-2.5 h-2.5 rounded-full bg-transparent group-hover:bg-[#0a66c2] transition-colors" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowLiAdPicker(false)}
                className="mt-5 px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-[#344054] hover:bg-gray-50 transition-all active:scale-[0.97]"
                style={{ fontWeight: 500 }}
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LinkedIn Ad Account Success Modal */}
      {showLiAdSuccess && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowLiAdSuccess(null)} />
          <div
            className="relative bg-white rounded-[24px] w-full max-w-[400px] overflow-hidden"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
          >
            <div className="p-8 pt-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
                LinkedIn Connected!
              </h3>
              <p className="text-[#667085] text-sm mb-1">
                <span style={{ fontWeight: 500 }}>{showLiAdSuccess}</span>
              </p>
              <p className="text-[#667085] text-sm mb-6 max-w-[300px] mx-auto">
                {showLiAdSuccess === 'LinkedIn Profile'
                  ? 'Your agents can now publish content directly to your LinkedIn profile.'
                  : 'Your agents can now manage LinkedIn Ad campaigns, targeting, and budgets.'}
              </p>
              <button
                onClick={() => setShowLiAdSuccess(null)}
                className="px-8 py-2.5 rounded-xl bg-[#be43ff] text-white text-sm hover:bg-[#a830e0] transition-all active:scale-[0.97]"
                style={{ fontWeight: 600 }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GA4 Success Modal */}
      {showGa4Success && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowGa4Success(null)} />
          <div
            className="relative bg-white rounded-[24px] w-full max-w-[400px] overflow-hidden"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
          >
            <div className="p-8 pt-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
                Analytics Connected
              </h3>
              <p className="text-[#667085] text-sm mb-1">
                <span style={{ fontWeight: 500 }}>{showGa4Success}</span>
              </p>
              <p className="text-[#667085] text-sm mb-6 max-w-[300px] mx-auto">
                Your agents now have access to real traffic data for this brand.
              </p>
              <button
                onClick={() => setShowGa4Success(null)}
                className="px-8 py-2.5 rounded-xl bg-[#be43ff] text-white text-sm hover:bg-[#a830e0] transition-all active:scale-[0.97]"
                style={{ fontWeight: 600 }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Left Sidebar
// ─────────────────────────────────────────────

function LeftSidebar({
  sessions,
  activeSessionId,
  artifacts,
  activeArtifactId,
  googleAdsSessions,
  activeGoogleAdsSessionId,
  onNewTask,
  onSessionClick,
  onArtifactClick,
  onBrandOS,
  onTools,
  onGoogleAds,
  onGoogleAdsSessionClick,
  onSettings,
  onUsage,
  onTasks,
  onOkrs,
  onDashboards,
  onFiles,
  onGrowthDiary,
  onNavigate,
  currentView,
  contentDrafts,
  onContentClick,
  availableBrands,
  activeBrandId,
  onBrandChange,
  planInfo,
  onUpgradeClick,
  notifications,
  notifUnread,
  onNotifMarkRead,
  onNotifMarkAllRead,
}: {
  sessions: SkillSession[];
  activeSessionId: string | null;
  artifacts: Artifact[];
  activeArtifactId: string | null;
  googleAdsSessions: GoogleAdsLocalSession[];
  activeGoogleAdsSessionId: string | null;
  onNewTask: () => void;
  onSessionClick: (s: SkillSession) => void;
  onArtifactClick: (a: Artifact) => void;
  onBrandOS: () => void;
  onTools: () => void;
  onGoogleAds: () => void;
  onGoogleAdsSessionClick: (id: string) => void;
  onSettings: () => void;
  onUsage: () => void;
  onTasks: () => void;
  onOkrs: () => void;
  onDashboards: () => void;
  onFiles: () => void;
  onGrowthDiary: () => void;
  onNavigate?: (view: string) => void;
  currentView: string;
  contentDrafts: Array<{ id: string; platform: string; title: string; status: string }>;
  onContentClick: (id: string) => void;
  availableBrands: BrandListItem[];
  activeBrandId?: string;
  onBrandChange: (brandId: string) => void;
  planInfo?: PlanInfo | null;
  onUpgradeClick?: () => void;
  notifications?: Notification[];
  notifUnread?: number;
  onNotifMarkRead?: (id: string) => void;
  onNotifMarkAllRead?: () => void;
}) {
  const [contentLimit, setContentLimit] = useState(10);
  const [notifOpen, setNotifOpen] = useState(false);

  const STATUS_DOT: Record<string, string> = {
    running: 'bg-[#a9f600] animate-pulse',
    queued: 'bg-yellow-400',
    completed: 'bg-[#10b981]',
    failed: 'bg-red-400',
  };

  return (
    <div className="w-[240px] shrink-0 bg-white border-r-0 flex flex-col">
      {/* Brand switcher */}
      {availableBrands.length > 0 && (
        <div className="px-3 pt-3 pb-1">
          <select
            value={activeBrandId || ''}
            onChange={e => onBrandChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-[13px] text-[#344054] font-medium focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40 cursor-pointer appearance-none"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}
          >
            {availableBrands.map(b => (
              <option key={b.brandId} value={b.brandId}>
                {b.brandName || b.brandId}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* New task */}
      <div className="p-3 pb-2">
        <button
          onClick={onNewTask}
          className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] text-[#101828] hover:bg-[#f8f5ff] transition-colors border border-gray-100"
          style={{ fontWeight: 600 }}
        >
          <Plus className="w-4 h-4 text-[#be43ff]" />
          New task
        </button>
      </div>

      {/* Brand OS shortcut */}
      <div className="px-3 pb-2">
        <button
          onClick={onBrandOS}
          className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054] transition-colors w-full"
          style={{ fontWeight: 500 }}
        >
          <BookOpen className="w-3.5 h-3.5" />
          Brand OS
        </button>
        <button
          onClick={onTasks}
          className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054] transition-colors w-full"
          style={{ fontWeight: 500 }}
        >
          <ClipboardList className="w-3.5 h-3.5" />
          Tasks
        </button>
        <button
          onClick={() => onNavigate?.('approvals')}
          className={cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors w-full",
            currentView === 'approvals' ? 'bg-[#fef2f2] text-[#ef4444]' : 'text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054]'
          )}
          style={{ fontWeight: 500 }}
        >
          <Shield className="w-3.5 h-3.5" />
          Approvals
        </button>
        <button
          onClick={() => onNavigate?.('campaigns')}
          className={cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors w-full",
            currentView === 'campaigns' ? 'bg-[#f4eefa] text-[#6d28d9]' : 'text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054]'
          )}
          style={{ fontWeight: 500 }}
        >
          <Megaphone className="w-3.5 h-3.5" />
          Campaigns
        </button>
        <button
          onClick={() => onNavigate?.('agents')}
          className={cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors w-full",
            currentView === 'agents' || currentView === 'marketplace' ? 'bg-[#f8f5ff] text-[#be43ff]' : 'text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054]'
          )}
          style={{ fontWeight: 500 }}
        >
          <Users className="w-3.5 h-3.5" />
          Agents
        </button>
        <button
          onClick={onOkrs}
          className={cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors w-full",
            currentView === 'okrs' ? 'bg-[#eef2ff] text-[#6366f1]' : 'text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054]'
          )}
          style={{ fontWeight: 500 }}
        >
          <Target className="w-3.5 h-3.5" />
          OKRs
        </button>
        <button
          onClick={onDashboards}
          className={cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors w-full",
            currentView === 'dashboards' ? 'bg-[#eef2ff] text-[#6366f1]' : 'text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054]'
          )}
          style={{ fontWeight: 500 }}
        >
          <BarChart3 className="w-3.5 h-3.5" />
          Dashboards
        </button>
        <button
          onClick={onFiles}
          className={cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors w-full",
            currentView === 'files' ? 'bg-[#eef2ff] text-[#6366f1]' : 'text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054]'
          )}
          style={{ fontWeight: 500 }}
        >
          <FolderOpen className="w-3.5 h-3.5" />
          Files
        </button>
        <button
          onClick={onTools}
          className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054] transition-colors w-full"
          style={{ fontWeight: 500 }}
        >
          <Wrench className="w-3.5 h-3.5" />
          Tools
        </button>
        <button
          onClick={onGrowthDiary}
          className={cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors w-full",
            currentView === 'growth-diary' ? 'bg-[#f8f5ff] text-[#be43ff]' : 'text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054]'
          )}
          style={{ fontWeight: 500 }}
        >
          <FileText className="w-3.5 h-3.5" />
          Growth Diary
        </button>
        <button
          onClick={onGoogleAds}
          className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054] transition-colors w-full"
          style={{ fontWeight: 500 }}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          Google Ads
        </button>
        <button onClick={onUsage} className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054] transition-colors w-full">
          <Zap className="w-3.5 h-3.5" />
          Usage
        </button>
        <button onClick={onSettings} className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] text-[#667085] hover:bg-[#f9fafb] hover:text-[#344054] transition-colors w-full">
          <Settings2 className="w-3.5 h-3.5" />
          Settings
        </button>
      </div>

      <div className="mx-3 border-t border-gray-100 my-1" />

      {/* Scrollable sidebar content — Recents, Reports, Content */}
      <div className="flex-1 overflow-y-auto min-h-0">

      {/* Recents */}
      {(sessions.length > 0 || googleAdsSessions.length > 0) && (
        <>
          <div className="px-5 pt-2 pb-1">
            <span className="text-[11px] text-[#98a2b3] uppercase tracking-wider" style={{ fontWeight: 600 }}>
              Recent
            </span>
          </div>
          <div className="px-2 pb-3">
            {/* Google Ads sessions */}
            {googleAdsSessions.slice(0, 10).map(s => (
              <button
                key={s.id}
                onClick={() => onGoogleAdsSessionClick(s.id)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-xl transition-all mb-0.5 flex items-start gap-2 group border',
                  activeGoogleAdsSessionId === s.id
                    ? 'bg-[#f0fdf4] border-[#34a853]/15'
                    : 'hover:bg-[#f9fafb] border-transparent'
                )}
              >
                <TrendingUp className={cn('w-3 h-3 mt-1 shrink-0', activeGoogleAdsSessionId === s.id ? 'text-[#34a853]' : 'text-[#98a2b3]')} />
                <span
                  className={cn('text-[12px] truncate', activeGoogleAdsSessionId === s.id ? 'text-[#16a34a]' : 'text-[#344054]')}
                  style={{ fontWeight: 500 }}
                >
                  {s.title}
                </span>
              </button>
            ))}
            {/* Skill sessions */}
            {sessions.slice(0, 20).map(s => {
              const elSec = s.completedAt
                ? Math.floor((new Date(s.completedAt).getTime() - new Date(s.startedAt).getTime()) / 1000)
                : s.status === 'running' || s.status === 'queued'
                  ? Math.floor((Date.now() - new Date(s.startedAt).getTime()) / 1000)
                  : 0;
              const elStr = `${Math.floor(elSec / 60)}:${(elSec % 60).toString().padStart(2, '0')}`;
              return (
                <button
                  key={s.id}
                  onClick={() => onSessionClick(s)}
                  className={cn(
                    'w-full text-left px-3 py-2 rounded-xl transition-all mb-0.5 flex items-start gap-2 group border',
                    activeSessionId === s.id
                      ? 'bg-[#f8f5ff] border-[#be43ff]/15'
                      : 'hover:bg-[#f9fafb] border-transparent'
                  )}
                >
                  <span
                    className={cn('w-1.5 h-1.5 rounded-full mt-1.5 shrink-0', STATUS_DOT[s.status] || 'bg-gray-300')}
                  />
                  <div className="flex-1 min-w-0">
                    <span
                      className={cn(
                        'text-[12px] truncate block',
                        activeSessionId === s.id ? 'text-[#be43ff]' : 'text-[#344054]'
                      )}
                      style={{ fontWeight: 500 }}
                    >
                      {s.skillId.replace(/-/g, ' ')}
                    </span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] text-[#c4c9d4] tabular-nums">{elStr}</span>
                      {s.tokensOutput != null && s.tokensOutput > 0 && (
                        <span className="text-[10px] text-[#c4c9d4]">· {s.tokensOutput.toLocaleString()} tok</span>
                      )}
                      {(s.status === 'running' || s.status === 'queued') && (
                        <Loader2 className="w-2.5 h-2.5 animate-spin text-[#a9f600]" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {sessions.length === 0 && googleAdsSessions.length === 0 && artifacts.length === 0 && (
        <div className="flex items-center justify-center px-4 py-8">
          <p className="text-[11px] text-[#c4c9d4] text-center">
            Your completed tasks will appear here
          </p>
        </div>
      )}

      {/* HTML Reports */}
      {artifacts.length > 0 && (
        <>
          <div className="mx-3 border-t border-gray-100 my-1" />
          <div className="px-5 pt-2 pb-1">
            <span className="text-[11px] text-[#98a2b3] uppercase tracking-wider" style={{ fontWeight: 600 }}>
              Reports
            </span>
          </div>
          <div className="px-2 pb-3">
            {artifacts.slice(0, 10).map(a => (
              <button
                key={a.id}
                onClick={() => onArtifactClick(a)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-xl transition-all mb-0.5 flex items-start gap-2 border',
                  activeArtifactId === a.id
                    ? 'bg-[#fff8f0] border-[#f97316]/15'
                    : 'hover:bg-[#f9fafb] border-transparent'
                )}
              >
                <FileText
                  className={cn(
                    'w-3.5 h-3.5 mt-0.5 shrink-0',
                    activeArtifactId === a.id ? 'text-[#f97316]' : 'text-[#98a2b3]'
                  )}
                />
                <span
                  className={cn(
                    'text-[12px] truncate',
                    activeArtifactId === a.id ? 'text-[#f97316]' : 'text-[#344054]'
                  )}
                  style={{ fontWeight: 500 }}
                >
                  {a.title}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Content Queue */}
      {contentDrafts.length > 0 && (
        <>
          <div className="mx-3 border-t border-gray-100 my-1" />
          <div className="px-5 pt-2 pb-1">
            <span className="text-[11px] text-[#98a2b3] uppercase tracking-wider" style={{ fontWeight: 600 }}>
              Content
            </span>
          </div>
          <div className="px-2 pb-3">
            {contentDrafts.slice(0, contentLimit).map(item => {
              const platformColors: Record<string, string> = {
                linkedin: '#0a66c2', instagram: '#e1306c', twitter: '#1d9bf0',
                tiktok: '#010101', facebook: '#1877f2', generic: '#667085',
              };
              const color = platformColors[item.platform] || '#667085';
              return (
                <button
                  key={item.id}
                  onClick={() => onContentClick(item.id)}
                  className="w-full text-left px-3 py-2 rounded-xl mb-0.5 flex items-start gap-2 hover:bg-[#f9fafb] transition-all cursor-pointer"
                >
                  <Send className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color }} />
                  <div className="flex-1 min-w-0">
                    <span className="text-[12px] text-[#344054] truncate block" style={{ fontWeight: 500 }}>
                      {item.title}
                    </span>
                    <span className={`text-[10px] ${item.status === 'saved' ? 'text-green-500' : item.status === 'published' ? 'text-green-600' : 'text-[#98a2b3]'}`}>
                      {item.status === 'draft' ? 'Draft' : item.status === 'saved' ? '✓ Saved' : item.status === 'published' ? 'Published' : item.status}
                    </span>
                  </div>
                </button>
              );
            })}
            {contentDrafts.length > contentLimit && (
              <button
                onClick={() => setContentLimit(prev => prev + 20)}
                className="w-full text-center py-2 text-[11px] text-[#667085] hover:text-[#344054] hover:bg-[#f9fafb] rounded-xl transition-all"
                style={{ fontWeight: 500 }}
              >
                See more ({contentDrafts.length - contentLimit} remaining)
              </button>
            )}
          </div>
        </>
      )}

      </div>{/* end scrollable sidebar content */}

      {/* Credit counter — pinned to bottom */}
      {planInfo && (
        <div className="shrink-0 border-t border-gray-100 px-3 py-2.5">
          <button
            onClick={onUpgradeClick}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#f8f5ff] transition-colors group"
          >
            <Zap className="w-3.5 h-3.5 text-[#be43ff]" />
            <span className="text-[12px] text-[#667085] group-hover:text-[#344054] transition-colors" style={{ fontWeight: 500 }}>
              {planInfo.creditsRemaining.toLocaleString()} credits
            </span>
            <div className="flex-1" />
            <div
              className="h-1.5 w-16 rounded-full bg-gray-100 overflow-hidden"
            >
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.max(2, Math.min(100, (planInfo.creditsRemaining / planInfo.creditsTotal) * 100))}%`,
                  backgroundColor: planInfo.creditsRemaining / planInfo.creditsTotal > 0.2 ? '#be43ff' : '#ef4444',
                }}
              />
            </div>
          </button>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────
//  Marketplace View — unified agents + skills
// ─────────────────────────────────────────────

const agentAvatarSrc = (agent: Agent) => avatarSrc(agent);

function MarketplaceView({
  agents,
  skillsByAgent,
  onChatWithAgent,
  onCallAgent,
  onGroupCall,
  onGroupChat,
  onRunSkill,
  onEditAgent,
}: {
  agents: Agent[];
  skillsByAgent: Record<string, Skill[]>;
  onChatWithAgent: (agent: Agent) => void;
  onCallAgent: (agent: Agent) => void;
  onGroupCall: () => void;
  onGroupChat: () => void;
  onRunSkill: (skill: Skill, agent: Agent) => void;
  onEditAgent?: (agent: Agent) => void;
}) {
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [agentStatuses, setAgentStatuses] = useState<Record<string, { status: 'idle' | 'working' | 'researching'; taskCount: number }>>({});

  useEffect(() => {
    coworkApi.agents.status().then(setAgentStatuses).catch(() => {});
    const interval = setInterval(() => {
      coworkApi.agents.status().then(setAgentStatuses).catch(() => {});
    }, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);
  const [agentUsage, setAgentUsage] = useState<Record<string, { level: 'high' | 'medium' | 'low' | 'idle'; cost: number }>>({});

  // Fetch usage data
  useEffect(() => {
    coworkApi.usage.all(7).then(data => {
      const usage: Record<string, { level: 'high' | 'medium' | 'low' | 'idle'; cost: number }> = {};
      for (const a of data.agents) {
        const cost = a.totalCostUsd;
        const level = cost > 1 ? 'high' : cost > 0.1 ? 'medium' : cost > 0 ? 'low' : 'idle';
        usage[a.agentId] = { level, cost };
      }
      setAgentUsage(usage);
    }).catch(() => {});
  }, []);

  const USAGE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    high: { bg: 'bg-red-50 border-red-200', text: 'text-red-600', label: '🔴 High' },
    medium: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-600', label: '🟡 Active' },
    low: { bg: 'bg-green-50 border-green-200', text: 'text-green-600', label: '🟢 Low' },
    idle: { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-400', label: '⚪ Idle' },
  };

  return (
    <div className="flex-1 overflow-y-auto px-8 py-8">
      <div className="max-w-[1000px] mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-[24px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>
              Your AI Marketing Team
            </h1>
            <p className="text-[14px] text-[#667085]">
              Each agent specialises in a different area of marketing. Pick one to run a skill, or chat with them directly.
            </p>
          </div>

          {/* Group action buttons */}
          <div className="flex items-center gap-2 shrink-0 ml-6 mt-1">
            <button
              onClick={onGroupCall}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[12px] border-2 border-dashed border-[#be43ff]/30 text-[#be43ff] hover:bg-[#be43ff]/5 transition-all"
              style={{ fontWeight: 600 }}
            >
              <Phone className="w-3.5 h-3.5" />
              Group Call
            </button>
            <button
              onClick={onGroupChat}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[12px] border-2 border-dashed border-[#10b981]/30 text-[#10b981] hover:bg-[#10b981]/5 transition-all"
              style={{ fontWeight: 600 }}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Group Chat
            </button>
          </div>
        </div>

        {/* Agent grid — 3 columns */}
        <div className="grid grid-cols-3 gap-4">
          {agents.map(agent => {
            const skills = skillsByAgent[agent.id] ?? [];

            const agentStatus = agentStatuses[agent.id];

            return (
              <div key={agent.id} className="relative">
                {/* Status indicator */}
                {agentStatus && agentStatus.status !== 'idle' && (
                  <div className={`absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    agentStatus.status === 'working'
                      ? 'bg-amber-50 text-amber-600 border border-amber-200'
                      : 'bg-blue-50 text-blue-500 border border-blue-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      agentStatus.status === 'working' ? 'bg-amber-500 animate-pulse' : 'bg-blue-400 animate-pulse'
                    }`} />
                    {agentStatus.status === 'working' ? `Working (${agentStatus.taskCount})` : 'Researching'}
                  </div>
                )}
                <AgentCard
                  agent={agent}
                  skills={skills}
                  onClick={(a) => setExpandedAgent(expandedAgent === a.id ? null : a.id)}
                  onChat={onChatWithAgent}
                  onCall={onCallAgent}
                  onEdit={onEditAgent}
                />
                {/* Expanded skills — clicking the card / "N skills" reveals
                    the agent's runnable skills inline. */}
                {expandedAgent === agent.id && (
                  <div className="mt-2 flex flex-col gap-1.5 rounded-xl border border-gray-100 bg-[#fafafa] p-2">
                    {skills.length === 0 ? (
                      <div className="text-[11px] text-[#98a2b3] px-2 py-1.5">No skills configured for this agent.</div>
                    ) : (
                      skills.map(skill => (
                        <button
                          key={skill.id}
                          onClick={() => onRunSkill(skill, agent)}
                          className="text-left px-3 py-2 rounded-lg bg-white border border-gray-100 hover:border-[#be43ff]/30 hover:bg-[#f8f5ff] transition-colors group/skill"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[12px] text-[#101828] font-semibold truncate">{skill.name}</span>
                            <span className="text-[10px] text-[#be43ff] opacity-0 group-hover/skill:opacity-100 transition-opacity shrink-0" style={{ fontWeight: 600 }}>Run →</span>
                          </div>
                          {skill.description && (
                            <div className="text-[11px] text-[#667085] line-clamp-2 mt-0.5">{skill.description}</div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// AgentDetailView removed — skills now shown inline in MarketplaceView

// ─────────────────────────────────────────────
//  Session View (streaming execution)
// ─────────────────────────────────────────────

function SessionView({
  session,
  agent,
  skill,
  onSessionUpdate,
  onArtifactReady,
}: {
  session: SkillSession;
  agent: Agent | null;
  skill: Skill | null;
  onSessionUpdate: (s: SkillSession) => void;
  onArtifactReady: (artifactId: string) => void;
}) {
  const [streamedText, setStreamedText] = useState('');
  const [events, setEvents] = useState<SessionEvent[]>(session.events);
  const [currentSession, setCurrentSession] = useState(session);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportElapsed, setReportElapsed] = useState(0);
  const reportTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [steps, setSteps] = useState<Array<{ label: string; status: 'done' | 'running' | 'pending' }>>([
    { label: 'Loading brand memory', status: 'pending' },
    { label: 'Assembling context', status: 'pending' },
    { label: 'Running analysis', status: 'pending' },
    { label: 'Generating output', status: 'pending' },
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Live elapsed timer
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Live token counters (from streaming events)
  const [liveTokensIn, setLiveTokensIn] = useState<number | null>(null);
  const [liveTokensOut, setLiveTokensOut] = useState(0);
  const tokenCharsRef = useRef(0);

  // Start elapsed timer when session is active
  useEffect(() => {
    const isActive = session.status === 'running' || session.status === 'queued';
    if (isActive) {
      const startMs = new Date(session.startedAt).getTime();
      setElapsed(Math.floor((Date.now() - startMs) / 1000));
      elapsedRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startMs) / 1000));
      }, 1000);
    } else if (session.completedAt) {
      setElapsed(Math.floor((new Date(session.completedAt).getTime() - new Date(session.startedAt).getTime()) / 1000));
    }
    return () => { if (elapsedRef.current) clearInterval(elapsedRef.current); };
  }, [session.id, session.status]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [streamedText, events]);

  useEffect(() => {
    // If session is already completed, just show stored result
    if (session.status === 'completed' || session.status === 'failed') {
      setCurrentSession(session);
      if (session.result) setStreamedText(session.result.content);
      setEvents(session.events);
      setSteps(prev => prev.map(s => ({ ...s, status: 'done' as const })));
      return;
    }

    // Start SSE stream
    const url = coworkApi.sessions.streamUrl(session.id);
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (e: MessageEvent<string>) => {
      if (e.data === '[DONE]') {
        es.close();
        if (elapsedRef.current) clearInterval(elapsedRef.current);
        // Refresh session from API to get final result
        coworkApi.sessions.get(session.id).then(updated => {
          setCurrentSession(updated);
          onSessionUpdate(updated);
          if (updated.result) setStreamedText(updated.result.content);
          if (updated.tokensInput) setLiveTokensIn(updated.tokensInput);
          if (updated.tokensOutput) setLiveTokensOut(updated.tokensOutput);
          if (updated.completedAt) {
            setElapsed(Math.floor((new Date(updated.completedAt).getTime() - new Date(updated.startedAt).getTime()) / 1000));
          }
          setSteps(prev => prev.map(s => ({ ...s, status: 'done' as const })));
        });
        return;
      }

      try {
        const event: SessionEvent = JSON.parse(e.data);
        setEvents(prev => [...prev, event]);

        if (event.type === 'text_delta' && typeof event.data.text === 'string') {
          setStreamedText(prev => prev + event.data.text);
          // Estimate live token count (~4 chars per token)
          tokenCharsRef.current += (event.data.text as string).length;
          setLiveTokensOut(Math.round(tokenCharsRef.current / 4));
          setSteps(prev => {
            const updated = [...prev];
            const runningIdx = updated.findIndex(s => s.status === 'running');
            if (runningIdx === -1) {
              const pendingIdx = updated.findIndex(s => s.status === 'pending');
              if (pendingIdx !== -1) updated[pendingIdx].status = 'running';
            }
            return updated;
          });
        }

        // Capture live token counts from session_completed event
        if (event.type === 'session_completed') {
          if (typeof event.data.tokensInput === 'number') setLiveTokensIn(event.data.tokensInput as number);
          if (typeof event.data.tokensOutput === 'number') setLiveTokensOut(event.data.tokensOutput as number);
        }

        if (event.type === 'context_assembled') {
          setSteps(prev => {
            const u = [...prev];
            u[0].status = 'done';
            u[1].status = 'running';
            return u;
          });
        }

        if (event.type === 'prompt_built') {
          setSteps(prev => {
            const u = [...prev];
            u[1].status = 'done';
            u[2].status = 'running';
            return u;
          });
        }

        if (event.type === 'claude_started') {
          setSteps(prev => {
            const u = [...prev];
            u[2].status = 'done';
            u[3].status = 'running';
            return u;
          });
        }
      } catch {
        // Ignore parse errors
      }
    };

    es.onerror = () => {
      es.close();
    };

    return () => {
      es.close();
    };
  }, [session.id]);

  const isRunning = currentSession.status === 'running' || currentSession.status === 'queued';
  const isFailed = currentSession.status === 'failed';

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Center: output */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="h-12 shrink-0 flex items-center justify-between px-5 border-b border-gray-100/60">
          <div className="flex items-center gap-2">
            {agent && (
              agentAvatarSrc(agent)
                ? <img src={agentAvatarSrc(agent)} alt={agent.name} className="w-5 h-5 rounded-md object-cover" />
                : <span className="text-[14px]">{agent.avatarEmoji}</span>
            )}
            <span className="text-[13px] text-[#344054]" style={{ fontWeight: 500 }}>
              {skill?.name ?? 'Skill run'}
            </span>
            {agent && (
              <span className="text-[12px] text-[#98a2b3]">— {agent.name}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Live elapsed timer */}
            <span className="text-[11px] text-[#98a2b3] tabular-nums" style={{ fontWeight: 500 }}>
              {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
            </span>
            {isRunning && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#a9f600]/15 text-[11px] text-[#5d7a00]" style={{ fontWeight: 600 }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#a9f600] animate-pulse" />
                Running
              </span>
            )}
            {currentSession.status === 'completed' && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ecfdf5] text-[11px] text-[#059669]" style={{ fontWeight: 600 }}>
                <CheckCircle2 className="w-3 h-3" />
                Done
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-[760px] mx-auto">
            {/* Agent avatar + streaming output */}
            {(streamedText || isRunning) && (
              <div className="flex gap-3">
                {agent && (
                  agentAvatarSrc(agent)
                    ? <img src={agentAvatarSrc(agent)} alt={agent.name} className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5" />
                    : <div
                        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-base"
                        style={{ background: `linear-gradient(135deg, ${agent.gradientFrom}, ${agent.gradientTo})` }}
                      >
                        {agent.avatarEmoji}
                      </div>
                )}
                <div className="flex-1 min-w-0">
                  {streamedText ? (
                    <MarkdownOutput text={streamedText} isStreaming={isRunning} />
                  ) : (
                    <div className="flex items-center gap-2 text-[13px] text-[#98a2b3]">
                      <Loader2 className="w-4 h-4 animate-spin text-[#be43ff]" />
                      {agent?.name ?? 'Agent'} is preparing your brief...
                    </div>
                  )}

                  {/* Feedback row (shown when done) */}
                  {!isRunning && streamedText && (
                    <div className="flex items-center gap-1.5 mt-4">
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-100 transition-colors" title="Copy">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-100 transition-colors" title="Good">
                        <ThumbsUp className="w-3.5 h-3.5" />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-100 transition-colors" title="Poor">
                        <ThumbsDown className="w-3.5 h-3.5" />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-100 transition-colors" title="Re-run">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Follow-up suggestions */}
            {!isRunning && (currentSession.result?.followUpSuggestions?.length ?? 0) > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-[11px] text-[#98a2b3] uppercase tracking-wider mb-2" style={{ fontWeight: 600 }}>
                  Suggested next steps
                </p>
                <div className="flex flex-wrap gap-2">
                  {currentSession.result?.followUpSuggestions?.map((s, i) => (
                    <span
                      key={i}
                      className="text-[12px] px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#667085] cursor-pointer hover:border-[#be43ff]/30 hover:text-[#be43ff] transition-colors"
                      style={{ fontWeight: 500 }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isFailed && (
              <div className="flex items-center gap-2 text-[13px] text-red-500 bg-red-50 rounded-xl p-4">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Skill execution failed. Check that your ANTHROPIC_API_KEY is set in the backend .env file.
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Right context panel */}
      <div className="w-[240px] shrink-0 border-l border-gray-100/60 bg-white overflow-y-auto">
        {/* Progress */}
        <div className="p-4 border-b border-gray-100">
          <h4 className="text-[11px] text-[#101828] uppercase tracking-wider mb-3" style={{ fontWeight: 700 }}>
            Progress
          </h4>
          <div className="space-y-0">
            {steps.map((step, idx) => (
              <div key={step.label} className="flex items-start gap-2.5 relative">
                {idx < steps.length - 1 && (
                  <div
                    className="absolute left-[7px] top-[18px] w-px h-[calc(100%)]"
                    style={{ background: step.status === 'done' ? '#be43ff' : '#e5e7eb' }}
                  />
                )}
                <div className="shrink-0 mt-0.5 relative z-10">
                  {step.status === 'done' ? (
                    <CheckCircle2 className="w-[15px] h-[15px] text-[#be43ff]" />
                  ) : step.status === 'running' ? (
                    <Loader2 className="w-[15px] h-[15px] text-[#a9f600] animate-spin" />
                  ) : (
                    <Circle className="w-[15px] h-[15px] text-[#d0d5dd]" />
                  )}
                </div>
                <div className="pb-3.5">
                  <span
                    className={cn(
                      'text-[12px] block',
                      step.status === 'done' ? 'text-[#344054]' : step.status === 'running' ? 'text-[#101828]' : 'text-[#98a2b3]'
                    )}
                    style={{ fontWeight: step.status === 'running' ? 600 : 500 }}
                  >
                    {step.label}
                  </span>
                  {step.status === 'running' && (
                    <span className="text-[10px] text-[#a9f600] block mt-0.5" style={{ fontWeight: 500 }}>
                      In progress…
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Context nodes used */}
        {skill && skill.requiredContextNodes.length > 0 && (
          <div className="p-4 border-b border-gray-100">
            <h4 className="text-[11px] text-[#101828] uppercase tracking-wider mb-2" style={{ fontWeight: 700 }}>
              Brand Context
            </h4>
            <div className="space-y-1">
              {skill.requiredContextNodes.map(node => (
                <div key={node} className="flex items-center gap-2 text-[11px] text-[#667085]">
                  <CheckCircle2 className="w-3 h-3 text-[#10b981] shrink-0" />
                  <span className="capitalize">{node.replace('_', ' ')} memory</span>
                </div>
              ))}
              {skill.requiresBigQueryData && (
                <div className="flex items-center gap-2 text-[11px] text-[#667085]">
                  <CheckCircle2 className="w-3 h-3 text-[#10b981] shrink-0" />
                  <span>Performance data</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Model + tokens + timing */}
        {skill && (
          <div className="p-4 border-b border-gray-100">
            <h4 className="text-[11px] text-[#101828] uppercase tracking-wider mb-2" style={{ fontWeight: 700 }}>
              Execution
            </h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[#98a2b3]">Model</span>
                <span className="text-[10px] text-[#667085]" style={{ fontWeight: 500 }}>{skill.claudeModel.replace('claude-', '').replace('-4-6', ' 4.6')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[#98a2b3]">Time</span>
                <span className="text-[10px] text-[#667085] tabular-nums" style={{ fontWeight: 500 }}>
                  {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
                  {isRunning && <Loader2 className="w-2.5 h-2.5 animate-spin inline ml-1 text-[#a9f600]" />}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[#98a2b3]">Tokens in</span>
                <span className="text-[10px] text-[#667085] tabular-nums" style={{ fontWeight: 500 }}>
                  {liveTokensIn != null ? liveTokensIn.toLocaleString() : (currentSession.tokensInput ? currentSession.tokensInput.toLocaleString() : '—')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[#98a2b3]">Tokens out</span>
                <span className="text-[10px] text-[#667085] tabular-nums" style={{ fontWeight: 500 }}>
                  {liveTokensOut > 0 ? `~${liveTokensOut.toLocaleString()}` : (currentSession.tokensOutput ? currentSession.tokensOutput.toLocaleString() : '—')}
                  {isRunning && liveTokensOut > 0 && <span className="text-[#a9f600] ml-1">●</span>}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Generate HTML Report */}
        {currentSession.status === 'completed' && (
          <div className="p-4">
            <h4 className="text-[11px] text-[#101828] uppercase tracking-wider mb-2" style={{ fontWeight: 700 }}>
              Report
            </h4>
            <p className="text-[10px] text-[#98a2b3] mb-3" style={{ lineHeight: '1.5' }}>
              Turn this output into a beautiful visual HTML report you can save and share.
            </p>
            <button
              onClick={async () => {
                setGeneratingReport(true);
                setReportError(null);
                setReportElapsed(0);
                reportTimerRef.current = setInterval(() => {
                  setReportElapsed(s => s + 1);
                }, 1000);
                try {
                  const { artifactId } = await coworkApi.artifacts.generate(currentSession.id);
                  onArtifactReady(artifactId);
                } catch (err) {
                  setReportError(err instanceof Error ? err.message : 'Failed to generate report');
                } finally {
                  setGeneratingReport(false);
                  if (reportTimerRef.current) clearInterval(reportTimerRef.current);
                }
              }}
              disabled={generatingReport}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-[12px] transition-all border"
              style={{
                background: generatingReport ? '#fff7ed' : 'linear-gradient(135deg, #f97316, #fb923c)',
                color: generatingReport ? '#f97316' : 'white',
                borderColor: generatingReport ? '#fed7aa' : 'transparent',
                fontWeight: 600,
              }}
            >
              {generatingReport ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Building report… {reportElapsed > 0 && `${reportElapsed}s`}
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  Generate Report
                </>
              )}
            </button>
            {generatingReport && (
              <p className="text-[10px] text-[#fb923c] mt-2 text-center">
                Designing your HTML report
              </p>
            )}
            {reportError && (
              <p className="text-[10px] text-red-400 mt-2">{reportError}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Artifact View — iframe HTML report
// ─────────────────────────────────────────────

function ArtifactView({
  artifact,
  agents,
  onBack,
  onDeleted,
}: {
  artifact: Artifact;
  agents: Agent[];
  onBack: () => void;
  onDeleted: (id: string) => void;
}) {
  const agent = agents.find(a => a.id === artifact.agentId);
  const [deleting, setDeleting] = useState(false);

  const handleOpenInTab = () => {
    if (!artifact.html) return;
    const blob = new Blob([artifact.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const handleDelete = async () => {
    if (!confirm('Delete this report? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await coworkApi.artifacts.delete(artifact.id);
      onDeleted(artifact.id);
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      {/* Header */}
      <div className="h-12 shrink-0 flex items-center justify-between px-5 border-b border-gray-100/60 bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-[11px] text-[#98a2b3] hover:text-[#667085] transition-colors shrink-0"
            style={{ fontWeight: 500 }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>
          <div className="w-px h-4 bg-gray-100" />
          {agent && (
            agentAvatarSrc(agent)
              ? <img src={agentAvatarSrc(agent)} alt={agent.name} className="w-5 h-5 rounded-md object-cover" />
              : <span className="text-[13px]">{agent.avatarEmoji}</span>
          )}
          <span
            className="text-[13px] text-[#344054] truncate"
            style={{ fontWeight: 600 }}
          >
            {artifact.title}
          </span>
          <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-[#fff7ed] text-[#f97316]" style={{ fontWeight: 600 }}>
            HTML Report
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleOpenInTab}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] text-[#667085] hover:bg-gray-100 transition-colors"
            style={{ fontWeight: 500 }}
            title="Open in new tab"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] text-[#f87171] hover:bg-red-50 transition-colors"
            title="Delete report"
          >
            {deleting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* iframe */}
      {artifact.html ? (
        <iframe
          srcDoc={artifact.html}
          className="flex-1 w-full border-0"
          title={artifact.title}
          sandbox="allow-scripts allow-popups"
        />
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-[#be43ff] animate-spin" />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Markdown Output renderer
// ─────────────────────────────────────────────

const MARKDOWN_COMPONENTS: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ children }) => (
    <h1 className="text-[20px] text-[#101828] mt-2 mb-3" style={{ fontWeight: 800 }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-[16px] text-[#101828] mt-6 mb-2 pb-1.5 border-b border-gray-100" style={{ fontWeight: 700 }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-[14px] text-[#101828] mt-5 mb-1.5" style={{ fontWeight: 700 }}>{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-[13px] text-[#101828] mt-4 mb-1" style={{ fontWeight: 700 }}>{children}</h4>
  ),
  p: ({ children }) => (
    <p className="text-[13px] text-[#344054] mb-3" style={{ lineHeight: '1.7' }}>{children}</p>
  ),
  ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => (
    <li className="text-[13px] text-[#344054]" style={{ lineHeight: '1.6' }}>{children}</li>
  ),
  strong: ({ children }) => (
    <strong style={{ fontWeight: 700, color: '#101828' }}>{children}</strong>
  ),
  em: ({ children }) => <em className="italic text-[#667085]">{children}</em>,
  code: ({ children, className }) => {
    if (className) {
      return (
        <code className="block text-[12px] text-[#344054]" style={{ fontFamily: 'monospace' }}>
          {children}
        </code>
      );
    }
    return (
      <code className="px-1.5 py-0.5 bg-[#f3f4f6] rounded text-[11px] text-[#374151]" style={{ fontFamily: 'monospace' }}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-[#f9fafb] border border-gray-100 rounded-xl p-4 mb-3 overflow-x-auto">
      {children}
    </pre>
  ),
  hr: () => <hr className="border-gray-100 my-4" />,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#be43ff]/30 pl-4 my-3 text-[#667085] italic">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4">
      <table className="w-full text-[12px] border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#f9fafb]">{children}</thead>,
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-[#344054] border border-gray-200" style={{ fontWeight: 600 }}>{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-[#667085] border border-gray-200">{children}</td>
  ),
  tr: ({ children }) => <tr className="hover:bg-[#f9fafb]/50">{children}</tr>,
};

function MarkdownOutput({ text, isStreaming }: { text: string; isStreaming: boolean }) {
  const clean = text
    .replace(/^\s*SUMMARY:.*$/gm, '')
    .replace(/^\s*NEXT_STEPS:.*$/gm, '')
    .trim();

  if (!isStreaming) {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {clean}
      </ReactMarkdown>
    );
  }

  // During streaming: split on newlines and only pass *completed* lines through
  // ReactMarkdown. The current partial line is rendered as plain text.
  // This avoids both raw-markdown-showing AND partial-construct artefacts.
  const lines = clean.split('\n');
  const completedText = lines.slice(0, -1).join('\n');
  const partialLine = lines[lines.length - 1];

  return (
    <div>
      {completedText && (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
          {completedText}
        </ReactMarkdown>
      )}
      {partialLine && (
        <p className="text-[13px] text-[#344054]" style={{ lineHeight: '1.7' }}>
          {partialLine}
        </p>
      )}
      <span className="inline-block w-1.5 h-4 bg-[#be43ff] animate-pulse rounded-sm ml-0.5" />
    </div>
  );
}

// ─────────────────────────────────────────────
//  Loading / Error states
// ─────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-6 h-6 text-[#be43ff] animate-spin" />
        <p className="text-[13px] text-[#98a2b3]">Loading your marketing team...</p>
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  const isAuthError = /token|auth|unauthorized|401/i.test(message);
  return (
    <div className="flex-1 flex items-center justify-center px-8">
      <div className="max-w-[480px] bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
        <p className="text-[14px] text-red-600" style={{ fontWeight: 600 }}>
          {isAuthError ? 'Your session has expired' : "We can't reach the server"}
        </p>
        <p className="text-[13px] text-red-500 mt-1">
          {isAuthError
            ? 'Please sign in again to continue.'
            : 'Check your connection and try again in a moment.'}
        </p>
        <button
          onClick={() => {
            if (isAuthError) {
              window.location.href = '/login';
            } else {
              window.location.reload();
            }
          }}
          className="mt-4 px-4 py-2 rounded-xl text-[13px] text-white bg-red-500 hover:bg-red-600 transition-colors"
          style={{ fontWeight: 600 }}
        >
          {isAuthError ? 'Sign in' : 'Retry'}
        </button>
      </div>
    </div>
  );
}
